import type { TFunction } from 'i18next';
import { toDiagnosticsState } from '@/services/diagnosticsSse';
import type {
  PingSseResult,
  TracerouteSseResult,
  SpeedSseResult,
  WifiSseResult,
} from '@/services/diagnosticsSse';
import type { DiagnosticHistoryType } from '@/services/diagnosticsService';

type ResultRecord = Record<string, unknown>;

/**
 * History rows carry the same result payload the live run reports, as an
 * object. A record stored as raw JSON is tolerated so an older row still reads.
 */
export function parseResult(raw: unknown): ResultRecord | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as ResultRecord;
  }

  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as ResultRecord;
      }
    } catch {
      // Not JSON after all — fall through to null.
    }
  }

  return null;
}

/** The state the device reported, with the scan's two radios collapsed. */
export function getResultState(result: ResultRecord | null): DiagnosticsState {
  if (!result) return 'None';

  const statuses = ['status', 'status1', 'status2']
    .map((key) => result[key])
    .filter((value): value is string => typeof value === 'string');

  return toDiagnosticsState(...statuses);
}

/** Reads a count out of a value the backend may send as a string or a number. */
function toCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Condenses a result payload into the few fragments worth showing in a table
 * row. Each tool reports different fields, so the projection is per type.
 */
export function getResultSummary(
  type: DiagnosticHistoryType,
  result: ResultRecord | null,
  t: TFunction
): string[] {
  if (!result) return [];

  switch (type) {
    case 'PING': {
      const ping = result as unknown as PingSseResult;
      const success = toCount(ping.successCount);
      const failure = toCount(ping.failureCount);
      const total = (success ?? 0) + (failure ?? 0);

      return [
        text(ping.host),
        ping.averageTime
          ? t('equipment.tools.history.summary.avgTime', {
              value: ping.averageTime,
            })
          : null,
        total > 0
          ? t('equipment.tools.history.summary.packets', {
              success: success ?? 0,
              total,
            })
          : null,
      ].filter((part): part is string => Boolean(part));
    }

    case 'TRACEROUTE': {
      const trace = result as unknown as TracerouteSseResult;

      return [
        text(trace.host),
        Array.isArray(trace.result)
          ? t('equipment.tools.history.summary.hops', {
              count: trace.result.length,
            })
          : null,
        trace.responseTime
          ? t('equipment.tools.history.summary.responseTime', {
              value: trace.responseTime,
            })
          : null,
      ].filter((part): part is string => Boolean(part));
    }

    case 'DOWNLOAD':
    case 'UPLOAD': {
      const speed = result as unknown as SpeedSseResult;
      const mbps = toCount(speed.speedMbps);

      return [
        mbps !== null
          ? t('equipment.tools.history.summary.speed', {
              value: mbps.toFixed(2),
            })
          : null,
        text(speed.url),
      ].filter((part): part is string => Boolean(part));
    }

    case 'WIFI': {
      const scan = result as unknown as WifiSseResult;

      return Array.isArray(scan.result)
        ? [
            t('equipment.tools.history.summary.networks', {
              count: scan.result.length,
            }),
          ]
        : [];
    }

    default:
      return [];
  }
}
